import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { MONTHS, monthShort, monthsCovered, spansNewYear, validateSeason } from './seasons.service';

const empty = { code: '', label: '', startMonth: 6, endMonth: 10 };

export default function SeasonModal({ open, season, existing = [], onClose, onSave }) {
    const [draft, setDraft] = useState(empty);
    const [issues, setIssues] = useState({});

    useEffect(() => {
        if (!open) return;
        setDraft(season ? { ...season, originalCode: season.code } : empty);
        setIssues({});
    }, [open, season]);

    const set = (field) => (event) => {
        const value = field === 'code' ? event.target.value.toUpperCase() : event.target.value;
        setDraft((prev) => ({ ...prev, [field]: value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = () => {
        const found = validateSeason(draft, existing);
        setIssues(found);
        if (Object.keys(found).length) return;

        onSave({
            code: draft.code.trim(),
            label: draft.label.trim(),
            startMonth: Number(draft.startMonth),
            endMonth: Number(draft.endMonth),
        });
    };

    const months = monthsCovered(draft);
    const wraps = spansNewYear(draft);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {season ? 'Edit season' : 'Add season'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <FieldLabel label="Code" required />
                        <TextField
                            fullWidth
                            value={draft.code}
                            onChange={set('code')}
                            placeholder="MAJOR_RAINS"
                            error={Boolean(issues.code)}
                            helperText={issues.code || 'Stored on every baseline for this season.'}
                            sx={formFieldSx}
                        />
                    </Box>
                    <Box>
                        <FieldLabel label="Display name" required />
                        <TextField
                            fullWidth
                            value={draft.label}
                            onChange={set('label')}
                            placeholder="Major rains"
                            error={Boolean(issues.label)}
                            helperText={issues.label}
                            sx={formFieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <FieldLabel label="First month" required />
                        <TextField
                            select
                            fullWidth
                            value={draft.startMonth}
                            onChange={set('startMonth')}
                            error={Boolean(issues.startMonth)}
                            helperText={issues.startMonth}
                            sx={formFieldSx}
                        >
                            {MONTHS.map((month) => (
                                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box>
                        <FieldLabel label="Last month" required />
                        <TextField
                            select
                            fullWidth
                            value={draft.endMonth}
                            onChange={set('endMonth')}
                            error={Boolean(issues.endMonth)}
                            helperText={issues.endMonth}
                            sx={formFieldSx}
                        >
                            {MONTHS.map((month) => (
                                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Box>

                <Box sx={{ backgroundColor: '#F8FAFF', border: '1px solid #DFE5F6', borderRadius: '12px', p: 2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#294190', mb: 0.5 }}>
                        Covers {months.length} month{months.length === 1 ? '' : 's'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: '#5B6472' }}>
                        {months.map(monthShort).join(' · ') || 'Choose both months.'}
                    </Typography>
                    {wraps && (
                        <Typography sx={{ fontSize: 13, color: '#8A5A00', mt: 1 }}>
                            Runs across the new year. Rainfall is counted from 1 {monthShort(draft.startMonth)} of
                            the earlier year.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {season ? 'Save season' : 'Add season'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
